import { AdvertisementCell } from "modules/advertising/tableCells/AdvertisementCell";
import { css } from "../../../../styled-system/css";
import { useMemo, useState } from "react";
import { createColumnBuilder } from "components/table/columnBuilder";
import { type AdvertisementQueryV4Row } from "../api/v4AdvReportByAd";
import { useUpdateAdState } from "modules/advertising/api/useUpdateAdState";
import { Switch } from "../../../components/fields/base/Switch";
import { errorToast } from "../../../utils/toasts";
import { useAppSelector } from "redux/hooks";
import { useDailyBudgetMinimum } from "components/core/blocks/AdPlan/utils";
import { useInitialValueSync } from "utils/formily";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { formatCurrency } from "utils/formatNumber";
import { Field } from "@formily/react";
import { NumberInput } from "../../../components/fields/formily/NumberInput";
import { CurrencyInput } from "../../../components/fields/formily/NumberInput";
import moment from "moment";
import { GOALS } from "../../../components/core/blocks/AdPlan/constants";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { servingStatusOptions } from "../../../views/advertising/adLifecycleFilters";

const columnHelper = createColumnBuilder<AdvertisementQueryV4Row>();

const isAdNotEditable = (ad: AdvertisementQueryV4Row) => {
  const nonManaged = ad.ad_goal === GOALS.IMPORTED_ADS.value;
  const enrolled = ad.non_trellis_enrolled_ad;
  // Dont show data for this field if ad is non-managed and not enrolled
  if (nonManaged && !enrolled) {
    return true;
  }
  return false;
};

const useAdReportColumns = () => {
  const acosOrRoas = useAcosOrRoas();
  const standardMetricIds = useStandardEntityMetrics("mediaPlan");
  const columns = useMemo(
    () => [
      // Fake columns for filtering purposes
      columnHelper.accessor("tags", {
        id: "tags",
        header: "Tags",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),
      columnHelper.accessor("category_id", {
        id: "category_id",
        header: "Category",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor("ad_status", {
        id: "ad_status",
        header: "Status",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("ntm_enrollment_status", {
        id: "ntm_enrollment_status",
        header: "NTM Ad Status",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("ad_source", {
        id: "ad_source",
        header: "Ad Source",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("program_id", {
        id: "program_id",
        header: "Bidding Rules",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("day_parting_method", {
        id: "day_parting_method",
        header: "Dayparting",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      // Fake column for filtering purposes end
      columnHelper.accessor("ad_name", {
        header: "Advertisement",
        enableHiding: false,
        cell: (props) => {
          return (
            <AdvertisementCell ad={props.row.original} className={css({ marginRight: "auto", overflowX: "hidden" })} />
          );
        },
        minSize: 75,
        size: 300,
        meta: {
          columnFilterOptions: {
            label: "Advertisement",
          },
        },
      }),
      columnHelper.accessor("state", {
        id: "state",
        header: "State",
        enableResizing: false,
        size: 70,
        cell: function StateCell({ getValue, row }) {
          const value = getValue();
          // Updating cache from mutation is a PITA, since query key is super complicated. Let's just use a local state
          const [switchState, setSwitchState] = useState(Boolean(value));
          if (Boolean(value) !== switchState) {
            setSwitchState(Boolean(value));
          }
          const stateMutation = useUpdateAdState();

          if (row.original.is_composite || ![0, 1].includes(value)) {
            return "-";
          }

          return (
            <Switch
              isSelected={switchState}
              isPending={stateMutation.isPending}
              onChange={(isSelected) => {
                stateMutation.mutate(
                  {
                    id: row.original.ad_id,
                    state: isSelected ? 1 : 0,
                  },
                  {
                    onSuccess: () => {
                      setSwitchState(isSelected);
                    },
                    onError: (error) => {
                      errorToast({
                        message: "Failed to update advertisement state",
                        error,
                      });
                    },
                  },
                );
              }}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
        },
      }),
      columnHelper.accessor("daily_budget", {
        id: "daily_budget",
        header: "Daily Budget",
        size: 90,
        cell: function Cell({ getValue, column, row }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          const minBudget = useDailyBudgetMinimum(row.original);
          useInitialValueSync(column.id, getValue());
          return (
            <Field
              name={`${column.id}`}
              initialValue={getValue()}
              validator={(value) => {
                if (minBudget && value < minBudget) {
                  return `Budget must be at least ${formatCurrency(minBudget, marketPlace, true)}`;
                }
              }}
              component={[
                CurrencyInput,
                {
                  variant: "inline",
                  className: css({ marginX: "small" }),
                },
              ]}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
          justify: "center",
        },
      }),
      acosOrRoas(
        columnHelper.accessor((r) => r.target_acos, {
          id: "target_acos",
          header: "ACOS Range Low",
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value) => {
                  if (value <= 0) {
                    return `ACOS Range Low must be greater than 0`;
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    className: css({ marginX: "small" }),
                    endIcon: "%",
                  },
                ]}
              />
            );
          },
          meta: {
            columnFilterOptions: {
              format: "percent",
            },
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
        columnHelper.accessor((r) => r.target_roas, {
          id: "target_roas",
          header: "ROAS Range High",
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value) => {
                  if (value <= 0) {
                    return `ROAS Range High must be greater than 0`;
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    className: css({ marginX: "small" }),
                  },
                ]}
              />
            );
          },
          meta: {
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
      ),
      acosOrRoas(
        columnHelper.accessor((r) => r.max_acos, {
          id: "max_acos",
          header: "ACOS Range High",
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value) => {
                  if (value <= 0) {
                    return `ACOS Range High must be greater than 0`;
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    className: css({ marginX: "small" }),
                    endIcon: "%",
                  },
                ]}
              />
            );
          },
          meta: {
            columnFilterOptions: {
              format: "percent",
            },
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
        columnHelper.accessor((r) => r.min_roas, {
          id: "min_roas",
          header: "ROAS Range Low",
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value) => {
                  if (value <= 0) {
                    return `ROAS Range Low must be greater than 0`;
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    className: css({ marginX: "small" }),
                  },
                ]}
              />
            );
          },
          meta: {
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
      ),
      columnHelper.accessor("created_datetime", {
        id: "created_datetime",
        header: "Created On",
        cell: ({ getValue }) => moment(getValue()).format("ll"),
        meta: {
          columnFilterOptions: {
            format: "date",
          },
        },
      }),
      columnHelper.accessor("serving_status", {
        id: "serving_status",
        header: "Serving Status",
        cell: ({ getValue }) => servingStatusOptions.find((option) => option.value === getValue())?.label,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
    ],
    [acosOrRoas, standardMetricIds],
  );
  return columns;
};

export default useAdReportColumns;
